// ─── Applying resolved photos to streamed markup ──────────────────────────────
// The generator writes photographs as descriptions rather than URLs:
//
//     <img data-photo="hands wedging clay at a wheel" width="1600" height="900">
//
// The server turns each description into a real image before saving, but the
// workspace preview is built from the streamed markup, which still only has the
// description. So the completion event carries a description → url map and this
// folds it into the pages the preview is already showing.
//
// Applied as a string transform on the page HTML rather than to the live DOM,
// deliberately: the code tab, the ZIP export and the visual editor all read the
// same source, so patching the DOM alone would leave three of them showing a
// site with no pictures in it.

/** `data-photo="…"` on an <img> that has no src yet. */
const IMG_RE = /<img\b((?:(?!\ssrc=)[^>])*?)\sdata-photo=["']([^"']+)["']((?:(?!\ssrc=)[^>])*?)>/gi;

/**
 * Fill in `src` for every image whose description the map knows.
 *
 * Images already carrying a src are left alone, so this is safe to run twice —
 * a refinement that re-streams a page it had already resolved does not end up
 * with two src attributes.
 *
 * @param {string} html
 * @param {Record<string,string>} photos description → url
 * @returns {string}
 */
export function applyPhotoMap(html, photos) {
  const src = String(html || '');
  if (!photos || !Object.keys(photos).length) return src;

  return src.replace(IMG_RE, (whole, before, query, after) => {
    const url = photos[String(query).trim()];
    if (!url) return whole;
    return `<img${before} src="${url}" data-photo="${query}"${after}>`;
  });
}

/** `@photo:<description>` — the same request, living in a string rather than a tag. */
const TOKEN_RE = /@photo:([^"'`<>\n]{3,120})/g;

/**
 * Fill in the photographs a data array asked for.
 *
 * A listings grid builds its cards in JavaScript, so its images are not <img>
 * tags in the markup at all — they are descriptions sitting in a data array,
 * written out at runtime. applyPhotoMap can never see them. Without this the
 * preview runs the generated script, the script writes `src="@photo:…"`, and
 * the whole grid renders as broken frames until the page is reloaded from the
 * database, which is the one copy the server had already resolved.
 *
 * Unknown descriptions are left as they are, so this is safe to run on a
 * partial map while photos are still arriving.
 *
 * @param {string} text usually the shared script
 * @param {Record<string,string>} photos description → url
 * @returns {string}
 */
export function applyPhotoTokens(text, photos) {
  const src = String(text || '');
  if (!photos || !Object.keys(photos).length) return src;
  return src.replace(TOKEN_RE, (whole, query) => photos[String(query).trim()] || whole);
}

/** The same, across every page of a site. */
export function applyPhotoMapToPages(pages, photos) {
  if (!Array.isArray(pages) || !photos || !Object.keys(photos).length) return pages;
  return pages.map((p) => ({ ...p, html: applyPhotoMap(p.html, photos) }));
}
